/**
 * installWork — the installer's on-site steps (FIELD-CREWS-01 phase 1, Seth
 * 2026-09-24), shown inside the job visit screen (jobVisit.tsx) for INSTALL
 * visits. New-construction crews get their own screens (phase 2).
 *
 *   · InstallMoneyCard — contract, add-ons sold today, paid so far, balance
 *     (decision 4: customer prices + the balance, never cost or margin).
 *   · EquipmentStep — each unit confirmed going in: the warehouse's serial
 *     matches, or the crew scans the one actually installed and says why
 *     (the office gets a note); accessories ticked; the old unit coming out.
 *   · ChangesStep — materials used vs planned (a reason whenever they differ),
 *     materials the crew needed that weren't planned, and add-ons sold on site
 *     at the pricebook price that the customer signs for on the iPad.
 *
 * Nothing here shows our cost. Data: features/jobs/installField.ts.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Camera, Check, ChevronRight, ClipboardList, Loader2, Minus, Package, PenLine, Plus, Search,
  ShoppingBag, Snowflake, Truck, Undo2, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { UnitLines } from "./UnitLines";
import { supabase } from "@/lib/supabase";
import { haptic } from "@/native/boot";
import {
  useJobEquipment, useJobMaterials, useChangeOrders,
  type Job, type JobEquipment, type JobMaterial,
  serialErrorText,
} from "@/features/jobs/jobs";
import {
  useConfirmUnitOnSite, useUnconfirmUnit, useToggleUnitInstalled,
  useRemovedEquipment, useAddRemovedEquipment, useDeleteRemovedEquipment, useUnitsOnFile,
  useSetMaterialUsed, useAddFieldMaterial, useRemoveFieldMaterial, useSellFieldAddon,
  readDataTag, KIND_FROM_TAG, sameBrand, sameModel, tidyBrand,
  SERIAL_REASONS, MATERIAL_REASONS, REMOVED_KINDS, DISPOSITION_LABEL,
  type RemovedUnit, type UnitOnFile, type TagRead,
} from "@/features/jobs/installField";
import { uploadOneFile, useJobFiles as useJobFilesForBefore } from "@/features/files/filesCore";
import { useQueryClient } from "@tanstack/react-query";
import { useItemSearch, type ItemHit } from "@/features/invoice/officeInvoice";
import { SignaturePad } from "./SignaturePad";
import {
  useRefrigerantKeys, useJobRefrigerant, chargeUnits, recoverUnits, ozText, REMOVED_KIND_KEY, useSaveRecovery,
} from "@/features/jobs/refrigerant";
import { RefrigerantUnitRow, RecoveryFields, RecoverySheet, recoveryReady, type RecoveryValue } from "./installRefrigerant";

export const card = "rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]";
export const input = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none";
const money = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qtyText = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));
const whenShort = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" }) : "";
const firstName = (s: string | null | undefined) => (s ?? "").trim().split(/\s+/)[0] || null;
const who = (email: string | null | undefined) => (email ? (email.includes("@") ? email.split("@")[0] : email) : null);

/** Full-screen sheet in the app's own look (header · scrolling body · footer). */
export function Sheet({ title, sub, onClose, children, footer }: { title: string; sub?: string | null; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[55] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[17px] font-extrabold tracking-tight text-slate-900">{title}</h2>
          {sub && <p className="truncate text-[11.5px] text-slate-500">{sub}</p>}
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">{children}</div>
      {footer && <footer className="border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">{footer}</footer>}
    </div>
  );
}

export function ReasonChips({ options, value, onPick }: { options: string[]; value: string; onPick: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((r) => (
        <button key={r} type="button" onClick={() => { haptic("light"); onPick(r); }}
          className={cn("rounded-full border px-3 py-1.5 text-[12.5px] font-semibold [touch-action:manipulation]",
            value === r ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 active:bg-slate-100")}>
          {r}
        </button>
      ))}
    </div>
  );
}

function Stepper({ value, onChange, step = 1 }: { value: number; onChange: (n: number) => void; step?: number }) {
  // The box keeps its own text so "2." and "1.5" can be typed; the number
  // underneath follows it, and the − / + buttons rewrite it.
  const [text, setText] = useState(qtyText(value));
  useEffect(() => {
    setText((t) => (t !== "" && Math.abs(Number(t) - value) < 1e-9 ? t : qtyText(value)));
  }, [value]);
  return (
    <div className="flex items-center gap-1.5">
      <button type="button" onClick={() => onChange(Math.max(0, Math.round((value - step) * 100) / 100))} aria-label="Less"
        className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 [touch-action:manipulation] active:bg-slate-200"><Minus className="h-4 w-4" /></button>
      <input inputMode="decimal" value={text}
        onChange={(e) => {
          const t = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
          setText(t);
          const n = Number(t);
          if (t !== "" && t !== "." && Number.isFinite(n)) onChange(n);
        }}
        className="h-11 w-20 rounded-xl border border-slate-200 bg-white text-center text-[17px] font-bold tabular-nums text-slate-900 focus:border-slate-400 focus:outline-none" />
      <button type="button" onClick={() => onChange(Math.round((value + step) * 100) / 100)} aria-label="More"
        className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 [touch-action:manipulation] active:bg-slate-200"><Plus className="h-4 w-4" /></button>
    </div>
  );
}

/* ------------------------------------------------------------- equipment */

const unitName = (u: JobEquipment) => `${u.category || "Equipment"}${(u.qty || 1) > 1 ? ` × ${u.qty}` : ""}`;
const unitModel = (u: JobEquipment) => [u.manufacturer, u.model].filter(Boolean).join(" ") || "model not set";

/** A data-tag photo already taken and read, handed to the sheet it belongs in. */
type TagHandoff = { file: File; read: TagRead; note?: string };
const tagKey = (s: string | null | undefined) => (s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/** Where a scanned tag belongs. Only hard evidence routes on its own — the
 *  warehouse's serial or the job's model (a new unit), a unit on file here or
 *  a plate printed 3+ years ago (an old one). "It's a furnace" alone doesn't:
 *  on a furnace swap the old and new tags both say furnace, so it asks. */
function routeTag(units: JobEquipment[], onFile: UnitOnFile[], taken: Set<string>, t: TagRead): { unit: JobEquipment } | "old" | null {
  const open = units.filter((u) => !u.install_confirmed_at);
  const same = (a: string | null | undefined, b: string | null | undefined) => !!tagKey(a) && tagKey(a) === tagKey(b);
  const bySerial = open.find((u) => same(u.serial, t.serial));
  if (bySerial) return { unit: bySerial };
  if (onFile.some((x) => !taken.has(x.key) && (same(x.serial, t.serial) || (!!t.model && same(x.model, t.model))))) return "old";
  const byModel = open.find((u) => same(u.model, t.model));
  if (byModel) return { unit: byModel };
  if (t.manufacture_year && t.manufacture_year <= new Date().getFullYear() - 3) return "old";
  return null;
}

export function EquipmentStep({ job, dispatchId, techFirst, empno = null }: { job: Job; dispatchId: string | null; techFirst: string | null; empno?: string | null }) {
  const { data: equip, isLoading } = useJobEquipment(job.id);
  // REFRIG-01: the outdoor / packaged units get Log the Charge (Settings decides which equipment holds refrigerant).
  const { data: refUses } = useRefrigerantKeys();
  const { data: refLogs } = useJobRefrigerant(job.id);
  const rows = equip ?? [];
  const units = rows.filter((e) => e.kind !== "accessory" && e.kind !== "material");
  const accessories = rows.filter((e) => e.kind === "accessory");
  const confirm = useConfirmUnitOnSite();
  const unconfirm = useUnconfirmUnit();
  const toggle = useToggleUnitInstalled();
  const [scan, setScan] = useState<JobEquipment | null>(null);
  const done = units.filter((u) => u.install_confirmed_at).length;
  const openUnits = units.filter((u) => !u.install_confirmed_at);
  const chargeIds = new Set(chargeUnits(units, refUses).map((u) => u.id));
  const chargeLog = (id: string) => (refLogs ?? []).find((l) => l.kind === "charge" && l.job_equipment_id === id);

  // "Scan Any Data Tag": one camera button for a multi-unit job. Pegasus reads
  // the tag and opens the right unit (or the old-unit sheet when the tag is a
  // unit on file here); only when it can't tell does it ask which one.
  const anyRef = useRef<HTMLInputElement>(null);
  const [routing, setRouting] = useState(false);
  const [handoff, setHandoff] = useState<TagHandoff | null>(null);
  const [choose, setChoose] = useState<TagHandoff | null>(null);
  const [outHandoff, setOutHandoff] = useState<TagHandoff | null>(null);
  const { data: removed } = useRemovedEquipment(job.id);
  const { data: onFile } = useUnitsOnFile(job.custno, job.locno);
  const taken = new Set((removed ?? []).map((r) => r.from_equipment).filter(Boolean) as string[]);
  const onAny = async (file: File) => {
    setRouting(true);
    try {
      const read = await readDataTag(file);
      const to = routeTag(units, onFile ?? [], taken, read);
      if (to === "old") setOutHandoff({ file, read });
      else if (to) {
        setHandoff({ file, read, note: `Matched to the ${unitName(to.unit)} from its tag — check it against the plate.` });
        setScan(to.unit);
      } else setChoose({ file, read });
    } finally {
      setRouting(false);
    }
  };
  // The chooser lists units of the tag's kind first.
  const chooseKind = choose ? KIND_FROM_TAG[choose.read.equipment_type] : undefined;
  const chooseList = [...openUnits].sort((a, b) => Number(kindFrom(b.category) === chooseKind) - Number(kindFrom(a.category) === chooseKind));

  return (
    <>
      <section className={card}>
        <div className="flex items-center justify-between gap-2">
          <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><Package className="h-4 w-4 text-slate-400" /> Going In</h4>
          {units.length > 0 && <span className={cn("m-chip", done === units.length ? "m-chip-good" : "")}>{done} of {units.length} confirmed</span>}
        </div>
        {openUnits.length > 1 && (
          <>
            <input ref={anyRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onAny(f); e.currentTarget.value = ""; }} />
            <button type="button" disabled={routing} onClick={() => anyRef.current?.click()} className="m-btn m-btn-tint mt-2.5 w-full">
              {routing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {routing ? "Reading the tag…" : "Scan Any Data Tag"}
            </button>
          </>
        )}
        {isLoading ? (
          <div className="mt-3 h-20 animate-pulse rounded-2xl bg-slate-100" />
        ) : units.length === 0 ? (
          <p className="mt-2 rounded-2xl bg-slate-50 px-3 py-2.5 text-xs text-slate-500">No equipment on the job yet — ask the office.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {units.map((u) => (
              <li key={u.id} className={cn("rounded-2xl px-3 py-3", u.install_confirmed_at ? "bg-emerald-50/70" : "bg-slate-50")}>
                <div className="flex items-start gap-2.5">
                  <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl", u.install_confirmed_at ? "bg-emerald-500 text-white" : "bg-white text-slate-400 ring-1 ring-slate-200")}>
                    {u.install_confirmed_at ? <Check className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                  </span>
                  {/* TECH-READ-01: the model and serial at reading size, both copyable. */}
                  <span className="min-w-0 flex-1">
                    <UnitLines
                      kind={unitName(u)}
                      brand={u.manufacturer}
                      model={u.model}
                      serial={u.serial}
                      serialLabel={u.install_confirmed_at ? "S/N" : "Warehouse S/N"}
                      serialFallback="No serial logged yet"
                    >
                      {u.install_confirmed_at && (
                        <span className="mt-1 block text-[15px] font-semibold text-emerald-700">Confirmed — it went in</span>
                      )}
                      {u.install_confirmed_at && (u.serial_before || u.model_before || u.manufacturer_before) && (
                        <span className="mt-1 block text-[15px] font-medium leading-snug text-amber-700">
                          Different from the job: {[
                            u.model_before || u.manufacturer_before ? `sold ${[u.manufacturer_before ?? u.manufacturer, u.model_before ?? u.model].filter(Boolean).join(" ")}` : null,
                            u.serial_before ? `warehouse S/N ${u.serial_before}` : null,
                          ].filter(Boolean).join(" · ")} — {u.serial_change_reason ?? "changed on site"}
                        </span>
                      )}
                    </UnitLines>
                  </span>
                  {u.install_confirmed_at && (
                    <button type="button" disabled={unconfirm.isPending} onClick={() => unconfirm.mutate(u)}
                      className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold text-slate-500 active:bg-white">
                      <Undo2 className="h-3.5 w-3.5" /> Undo
                    </button>
                  )}
                </div>
                {!u.install_confirmed_at && (
                  u.serial ? (
                    <div className="mt-2.5 grid grid-cols-2 gap-2">
                      <button type="button" disabled={confirm.isPending}
                        onClick={() => confirm.mutate({ unit: u, techFirst }, { onSuccess: () => haptic("success") })}
                        className="m-btn m-btn-good">
                        {confirm.isPending && confirm.variables?.unit.id === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} It Matches
                      </button>
                      <button type="button" onClick={() => setScan(u)} className="m-btn m-btn-soft">Different Unit</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setScan(u)} className="m-btn m-btn-tint mt-2.5 w-full">
                      <Camera className="h-4 w-4" /> Scan the Data Tag
                    </button>
                  )
                )}
                {chargeIds.has(u.id) && (u.install_confirmed_at || chargeLog(u.id)) && (
                  <RefrigerantUnitRow job={job} unit={u} log={chargeLog(u.id)} dispatchId={dispatchId} empno={empno} techFirst={techFirst} />
                )}
              </li>
            ))}
          </ul>
        )}
        {confirm.isError && <p className="mt-2 text-center text-[12px] font-semibold text-rose-600">{/SERIAL_IN_USE/.test(String((confirm.error as Error)?.message)) ? `${serialErrorText(confirm.error)} — the office can move it to this job.` : "Didn\u2019t save \u2014 check the signal and try again."}</p>}

        {accessories.length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Accessories</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {accessories.map((a) => {
                const on = !!a.install_confirmed_at;
                return (
                  <button key={a.id} type="button" disabled={toggle.isPending}
                    onClick={() => { haptic("light"); toggle.mutate({ id: a.id, on: !on }); }}
                    className={cn("flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold [touch-action:manipulation]",
                      on ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 active:bg-slate-100")}>
                    {on ? <Check className="h-3.5 w-3.5" /> : <span className="h-3.5 w-3.5 rounded-full border border-slate-300" />}
                    {[a.category, a.model].filter(Boolean).join(" · ") || "Accessory"}{(a.qty || 1) > 1 ? ` × ${a.qty}` : ""}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <ComingOutCard job={job} dispatchId={dispatchId} empno={empno} />

      {scan && <SerialSheet unit={scan} jobId={job.id} techFirst={techFirst} initial={handoff} onClose={() => { setScan(null); setHandoff(null); }} />}
      {outHandoff && <ComingOutSheet job={job} dispatchId={dispatchId} empno={empno} taken={taken} initial={outHandoff} onClose={() => setOutHandoff(null)} />}
      {choose && (
        <Sheet title="Which Unit Is This Tag From?" sub={[tidyBrand(choose.read.manufacturer), choose.read.model].filter(Boolean).join(" ") || "Couldn't read the tag"} onClose={() => setChoose(null)}>
          <ul className="space-y-2">
            {chooseList.map((u) => (
              <li key={u.id}>
                <button type="button" onClick={() => { setHandoff({ file: choose.file, read: choose.read }); setScan(u); setChoose(null); }}
                  className="flex w-full items-center gap-3 rounded-2xl bg-white px-3.5 py-3 text-left ring-1 ring-slate-200 [touch-action:manipulation] active:bg-slate-50">
                  <Package className="h-5 w-5 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1">
                    <span className="m-unit-kind block">{unitName(u)}</span>
                    <span className="m-unit-model block">{unitModel(u)}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                </button>
              </li>
            ))}
            <li>
              <button type="button" onClick={() => { setOutHandoff(choose); setChoose(null); }}
                className="flex w-full items-center gap-3 rounded-2xl bg-white px-3.5 py-3 text-left ring-1 ring-slate-200 [touch-action:manipulation] active:bg-slate-50">
                <Truck className="h-5 w-5 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 text-[14px] font-bold text-slate-800">An Old Unit Coming Out</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
              </button>
            </li>
          </ul>
        </Sheet>
      )}
    </>
  );
}

/** The unit actually going in: photograph its data tag (Pegasus reads brand,
 *  model and serial) or type them. When it isn't the unit on the job — a
 *  different model, or not the serial the warehouse logged — the crew says
 *  why; the job's equipment updates and the office gets a note. */
function SerialSheet({ unit, jobId, techFirst, initial, onClose }: {
  unit: JobEquipment; jobId: string; techFirst: string | null;
  /** A tag photo already taken + read by "Scan Any Data Tag" (routed here). */
  initial?: TagHandoff | null;
  onClose: () => void;
}) {
  const confirm = useConfirmUnitOnSite();
  const fileRef = useRef<HTMLInputElement>(null);
  const [brand, setBrand] = useState(unit.manufacturer ?? "");
  const [model, setModel] = useState(unit.model ?? "");
  const [serial, setSerial] = useState("");
  const [reason, setReason] = useState("");
  const [photo, setPhoto] = useState<{ url: string; path: string } | null>(null);
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState<string | null>(null);
  // REFRIG-01: what the plate says it holds (saved on the unit with the serial).
  const [plate, setPlate] = useState<{ refrigerant: string; oz: number | null; ft: number | null } | null>(null);
  const was = (unit.serial ?? "").trim().toUpperCase();
  const typed = serial.trim().toUpperCase();
  const serialDiffers = !!was && !!typed && typed !== was;
  // Blank on the job = just filling it in; tag wording is never "different".
  const modelDiffers = !sameModel(model, unit.model);
  const brandDiffers = !sameBrand(brand, unit.manufacturer);
  const differs = serialDiffers || modelDiffers || brandDiffers;
  const canSave = !!typed && (!differs || !!reason) && !confirm.isPending && !reading;

  const onPick = async (file: File, pre?: TagRead, routed?: string) => {
    setReading(true);
    setReadNote(null);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
      const path = `job/${jobId}/${unit.id}-${Date.now()}.${ext}`;
      const [up, t] = await Promise.all([
        supabase.storage.from("job-media").upload(path, file, { upsert: true, contentType: file.type || "image/jpeg" }),
        pre ? Promise.resolve(pre) : readDataTag(file),
      ]);
      if (up.error) throw up.error;
      setPhoto({ url: URL.createObjectURL(file), path });
      if (t.refrigerant || t.factory_charge_oz != null) setPlate({ refrigerant: t.refrigerant, oz: t.factory_charge_oz, ft: t.factory_lineset_ft });
      if (t.serial) setSerial(t.serial);
      // Same unit as the job → keep the job's spelling; otherwise the tag's.
      if (t.model) setModel(unit.model && sameModel(t.model, unit.model) ? unit.model : t.model);
      if (t.manufacturer) setBrand(unit.manufacturer && sameBrand(t.manufacturer, unit.manufacturer) ? unit.manufacturer : tidyBrand(t.manufacturer));
      setReadNote(t.serial || t.model ? (routed ?? "Read from the tag — check it against the plate.") : "Couldn't read the tag — type it in.");
    } catch {
      setReadNote("Couldn't read that photo — type it in.");
    } finally {
      setReading(false);
    }
  };
  // Routed from "Scan Any Data Tag": the photo is taken and read already.
  const started = useRef(false);
  useEffect(() => {
    if (!initial || started.current) return;
    started.current = true;
    void onPick(initial.file, initial.read, initial.note);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = () =>
    confirm.mutate(
      { unit, serial: typed, model: model.trim() || null, manufacturer: brand.trim() || null, reason: differs ? reason : null, photoPath: photo?.path ?? null, techFirst,
        refrigerant: plate?.refrigerant || null, factoryChargeOz: plate?.oz ?? null, factoryLinesetFt: plate?.ft ?? null },
      { onSuccess: () => { haptic("success"); onClose(); } },
    );

  return (
    <Sheet title="The Unit Going In" sub={`${unitName(unit)} · ${unitModel(unit)}`} onClose={onClose}
      footer={
        <button type="button" disabled={!canSave} onClick={save} className="m-btn m-btn-good m-btn-big w-full">
          {confirm.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />} Confirm — This Unit Is Going In
        </button>
      }>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPick(f); e.currentTarget.value = ""; }} />
      <button type="button" disabled={reading} onClick={() => fileRef.current?.click()} className="m-btn m-btn-tint m-btn-big w-full">
        {reading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Camera className="h-[18px] w-[18px]" />}
        {reading ? "Reading the tag…" : photo ? "Retake the Data Tag" : "Photograph the Data Tag"}
      </button>
      {photo && <img src={photo.url} alt="Data tag" className="max-h-52 w-full rounded-2xl border border-slate-200 bg-white object-contain" />}
      {readNote && <p className="px-1 text-[12px] font-medium text-slate-500">{readNote}</p>}
      {plate && (plate.refrigerant || plate.oz != null) && (
        <p className="flex items-center gap-2 rounded-2xl bg-sky-50 px-3 py-2 text-[12.5px] font-semibold text-sky-900">
          <Snowflake className="h-4 w-4 shrink-0" />
          {[plate.refrigerant, plate.oz != null ? `factory charge ${ozText(plate.oz)}` : null, plate.ft ? `for ${plate.ft} ft` : null].filter(Boolean).join(" · ")}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[12px] font-bold text-slate-600">Brand</span>
          <input value={brand} onChange={(e) => setBrand(e.target.value)} className={cn(input, "mt-1")} />
        </label>
        <label className="block">
          <span className="text-[12px] font-bold text-slate-600">Model</span>
          <input value={model} onChange={(e) => setModel(e.target.value)} autoCapitalize="characters" autoCorrect="off" spellCheck={false} className={cn(input, "mt-1 font-mono")} />
        </label>
      </div>
      <label className="block">
        <span className="text-[12px] font-bold text-slate-600">Serial</span>
        <input id="serial-in" value={serial} onChange={(e) => setSerial(e.target.value.toUpperCase())} autoCapitalize="characters" autoCorrect="off" spellCheck={false}
          placeholder={was ? `Warehouse logged ${was}` : "Type or read from the tag"}
          className="mt-1 w-full rounded-2xl border-2 border-slate-300 bg-white px-3.5 py-3 font-mono text-[19px] font-bold uppercase tracking-wide text-slate-900 placeholder:font-sans placeholder:text-[14px] placeholder:font-medium placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400 focus:border-slate-500 focus:outline-none" />
      </label>
      {differs && (
        <div className="rounded-2xl bg-amber-50 p-3">
          <div className="text-[12.5px] font-bold text-amber-900">
            {modelDiffers || brandDiffers ? `Not the unit on the job (${[unit.manufacturer, unit.model].filter(Boolean).join(" ") || "no model set"}).` : `Not the serial the warehouse logged (${was}).`} Why?
          </div>
          <div className="mt-2"><ReasonChips options={SERIAL_REASONS} value={reason} onPick={setReason} /></div>
        </div>
      )}
      {confirm.isError && <p className="text-center text-[12px] font-semibold text-rose-600">{/SERIAL_IN_USE/.test(String((confirm.error as Error)?.message)) ? `${serialErrorText(confirm.error)} — the office can move it to this job.` : "Didn\u2019t save \u2014 check the signal and try again."}</p>}
    </Sheet>
  );
}

/* ------------------------------------------------------------ coming out */

function kindFrom(text: string | null | undefined): string {
  const t = (text ?? "").toLowerCase();
  if (/furn/.test(t)) return "Furnace";
  if (/heat\s*pump|\bhp\b/.test(t)) return "Heat Pump";
  if (/coil/.test(t)) return "Coil";
  if (/air\s*handl|\bah\b/.test(t)) return "Air Handler";
  if (/boiler/.test(t)) return "Boiler";
  if (/water/.test(t)) return "Water Heater";
  if (/a\/?c\b|cond|air\s*cond|\bac\b/.test(t)) return "Air Conditioner";
  return text?.trim() ? "Other" : "";
}

function ComingOutCard({ job, dispatchId, empno = null }: { job: Job; dispatchId: string | null; empno?: string | null }) {
  const { data: removed } = useRemovedEquipment(job.id);
  const del = useDeleteRemovedEquipment();
  const [open, setOpen] = useState(false);
  const [recFor, setRecFor] = useState<RemovedUnit | null>(null);
  const list = removed ?? [];
  // REFRIG-01: an old AC / heat pump (or a lone coil) gets its refrigerant recovered — the EPA record.
  const { data: refUses } = useRefrigerantKeys();
  const { data: refLogs } = useJobRefrigerant(job.id);
  const mustRecover = new Set(recoverUnits(list, refUses).map((r) => r.id));
  const recOf = (id: string) => (refLogs ?? []).find((l) => l.kind === "recover" && l.removed_equipment_id === id);
  return (
    <section className={card}>
      <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><Truck className="h-4 w-4 text-slate-400" /> Coming Out</h4>
      {list.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {list.map((r) => (
            <li key={r.id} className="flex items-start gap-2.5 rounded-2xl bg-slate-50 px-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-bold text-slate-800">{r.category || "Old unit"}{r.brand || r.model ? ` · ${[r.brand, r.model].filter(Boolean).join(" ")}` : ""}</span>
                <span className="block text-[11.5px] text-slate-500">
                  {[r.serial ? `S/N ${r.serial}` : null, r.year_made ? `made ${r.year_made}` : null, DISPOSITION_LABEL[r.disposition], r.tag_file_id ? "tag photo" : null].filter(Boolean).join(" · ")}
                </span>
                {r.notes && <span className="block text-[11.5px] text-slate-500">“{r.notes}”</span>}
                {(() => {
                  const rec = recOf(r.id);
                  if (rec) {
                    return (
                      <button type="button" onClick={() => setRecFor(r)} className="mt-1 flex items-center gap-1.5 text-left text-[12px] font-semibold text-emerald-700">
                        <Snowflake className="h-3.5 w-3.5 shrink-0" />
                        {rec.empty ? `${rec.refrigerant} · it was empty` : `${rec.refrigerant} · ${ozText(rec.oz)} recovered`}
                      </button>
                    );
                  }
                  if (!r.equip_key || !refUses?.has(r.equip_key)) return null;
                  return (
                    <button type="button" onClick={() => setRecFor(r)}
                      className={cn("m-btn mt-1.5 !px-3 !py-1.5 !text-[12.5px]", mustRecover.has(r.id) ? "m-btn-tint" : "m-btn-soft")}>
                      <Snowflake className="h-3.5 w-3.5" /> Log Refrigerant Recovered
                    </button>
                  );
                })()}
              </span>
              <button type="button" disabled={del.isPending} onClick={() => del.mutate(r.id)} aria-label="Remove"
                className="shrink-0 rounded-lg p-1.5 text-slate-400 active:bg-white"><X className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" onClick={() => setOpen(true)} className={cn("m-btn mt-3 w-full", list.length ? "m-btn-soft" : "m-btn-tint")}>
        <Plus className="h-4 w-4" /> {list.length ? "Add Another Old Unit" : "Add the Old Unit"}
      </button>
      {open && <ComingOutSheet job={job} dispatchId={dispatchId} empno={empno} taken={new Set(list.map((r) => r.from_equipment).filter(Boolean) as string[])} onClose={() => setOpen(false)} />}
      {recFor && <RecoverySheet job={job} unit={recFor} log={recOf(recFor.id)} dispatchId={dispatchId} empno={empno} onClose={() => setRecFor(null)} />}
    </section>
  );
}

function ComingOutSheet({ job, dispatchId, empno = null, taken, initial, onClose }: {
  job: Job; dispatchId: string | null; empno?: string | null; taken: Set<string>;
  /** A tag photo already taken + read by "Scan Any Data Tag" (routed here). */
  initial?: TagHandoff | null;
  onClose: () => void;
}) {
  const add = useAddRemovedEquipment();
  const saveRec = useSaveRecovery();
  const { data: refUses } = useRefrigerantKeys();
  const [rec, setRec] = useState<RecoveryValue>({ refrigerant: null, oz: 0, empty: false, tankId: null, nameplateOz: null });
  const [recOpen, setRecOpen] = useState(false);
  const qc = useQueryClient();
  const { data: onFile } = useUnitsOnFile(job.custno, job.locno);
  const { data: jobUnits } = useJobEquipment(job.id);
  const fileRef = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState<string | null>(null);
  const [tagUrl, setTagUrl] = useState<string | null>(null);
  // The tag photo is filed with the job when the old unit is SAVED — backing
  // out (or snapping the new unit's tag by mistake) leaves nothing behind.
  const [tagFile, setTagFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState<Omit<RemovedUnit, "id" | "created_by" | "created_at" | "job_id" | "dispatch_id">>({
    category: "", brand: "", model: "", serial: "", year_made: null, disposition: "haul_away", from_equipment: null, notes: "", tag_file_id: null,
  });
  const picks = (onFile ?? []).filter((u) => !taken.has(u.key)).slice(0, 6);
  const pick = (u: UnitOnFile) => {
    haptic("light");
    setF((x) => ({ ...x, category: kindFrom(u.category) || x.category, brand: u.brand ?? "", model: u.model ?? "", serial: u.serial ?? "", year_made: u.year, from_equipment: u.key }));
  };

  // The old unit's data tag: Pegasus reads what it is, brand, model, serial
  // and year; a unit on file with the same serial (or model) is picked for
  // them. The photo stays on the job.
  const onTag = async (file: File, pre?: TagRead) => {
    setReading(true);
    setReadNote(null);
    setNewWarn(null);
    setTagUrl(URL.createObjectURL(file));
    setTagFile(file);
    try {
      const t = pre ?? (await readDataTag(file));
      const k = (s: string | null | undefined) => (s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      const eq = (a: string | null | undefined, b: string | null | undefined) => !!k(a) && k(a) === k(b);
      const match = (onFile ?? []).find((u) => !taken.has(u.key) && (eq(u.serial, t.serial) || (!!t.model && eq(u.model, t.model))));
      setF((x) => ({
        ...x,
        category: KIND_FROM_TAG[t.equipment_type] || (match ? kindFrom(match.category) : "") || x.category,
        // The file's spelling when it's the same maker ("Carrier", not "CARRIER CORPORATION").
        brand: (match?.brand && sameBrand(match.brand, t.manufacturer) ? match.brand : tidyBrand(t.manufacturer)) || match?.brand || x.brand,
        model: t.model || match?.model || x.model,
        serial: t.serial || match?.serial || x.serial,
        year_made: t.manufacture_year ?? match?.year ?? x.year_made,
        from_equipment: match?.key ?? x.from_equipment,
      }));
      if (t.refrigerant || t.factory_charge_oz != null) {
        setRec((x) => ({ ...x, refrigerant: t.refrigerant || x.refrigerant, nameplateOz: t.factory_charge_oz ?? x.nameplateOz }));
      }
      setReadNote(t.serial || t.model
        ? (match ? "Read from the tag — matched a unit on file." : "Read from the tag — check it against the plate.")
        : "Couldn't read the tag — type what you can see.");
      // Snapped the NEW unit's tag by mistake? Say so before it's logged as old.
      const isNew = (jobUnits ?? []).find((u) => u.kind !== "accessory" && u.kind !== "material" && (eq(u.serial, t.serial) || (!!t.model && eq(u.model, t.model))));
      if (isNew) setNewWarn(`This tag matches the new ${isNew.category || "unit"} going in, not an old one.`);
    } finally {
      setReading(false);
    }
  };
  const [newWarn, setNewWarn] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (!initial || started.current) return;
    started.current = true;
    void onTag(initial.file, initial.read);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ok = !!(f.category || f.brand || f.model || f.serial);
  // Refrigerant Recovered shows for an old AC / heat pump; for a coil or air
  // handler it's one tap away (with an old AC the gas came out of the AC).
  const kindKey = REMOVED_KIND_KEY[f.category ?? ""] ?? null;
  const holdsRef = !!kindKey && !!refUses?.has(kindKey);
  const showRec = holdsRef && (kindKey === "ac" || kindKey === "heat-pump" || recOpen);
  const save = async () => {
    setSaving(true);
    try {
      let tagId = f.tag_file_id;
      if (tagFile) {
        const row = await uploadOneFile({ custno: job.custno, locno: job.locno, job_id: job.id, caption: "Data tag — old unit", doc_tag: "photo", origin: "tech" }, tagFile).catch(() => null);
        if (row) { tagId = row.id; void qc.invalidateQueries({ queryKey: ["files"] }); }
      }
      const newId = await add.mutateAsync({
        ...f, tag_file_id: tagId, job_id: job.id, dispatch_id: dispatchId,
        refrigerant: holdsRef ? (rec.refrigerant || null) : null, nameplate_charge_oz: holdsRef ? rec.nameplateOz : null,
      });
      // Recovered already? Log it now; otherwise it's one tap on the old unit later.
      if (showRec && recoveryReady(rec)) {
        await saveRec.mutateAsync({ jobId: job.id, dispatchId, removedId: newId, refrigerant: rec.refrigerant!.trim(), oz: rec.oz, empty: rec.empty, tankId: rec.tankId, nameplateOz: rec.nameplateOz, note: null, empno }).catch(() => null);
      }
      haptic("success");
      onClose();
    } catch {
      /* add.isError shows the retry line */
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet title="Old Unit Coming Out" sub={job.customer_name ?? job.title} onClose={onClose}
      footer={
        <button type="button" disabled={!ok || saving || reading} onClick={() => void save()}
          className="m-btn m-btn-primary m-btn-big w-full">
          {saving ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />} Save the Old Unit
        </button>
      }>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { const file = e.target.files?.[0]; if (file) void onTag(file); e.currentTarget.value = ""; }} />
      <button type="button" disabled={reading} onClick={() => fileRef.current?.click()} className="m-btn m-btn-tint m-btn-big w-full">
        {reading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Camera className="h-[18px] w-[18px]" />}
        {reading ? "Reading the tag…" : tagUrl ? "Retake the Data Tag" : "Photograph the Data Tag"}
      </button>
      {tagUrl && <img src={tagUrl} alt="Old unit data tag" className="max-h-44 w-full rounded-2xl border border-slate-200 bg-white object-contain" />}
      {readNote && <p className="px-1 text-[12px] font-medium text-slate-500">{readNote}</p>}
      {newWarn && <p className="rounded-2xl bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-800">{newWarn}</p>}
      {picks.length > 0 && (
        <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-200">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">On File at This Address</div>
          <ul className="mt-1.5 space-y-1">
            {picks.map((u) => (
              <li key={u.key}>
                <button type="button" onClick={() => pick(u)}
                  className={cn("flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left [touch-action:manipulation]", f.from_equipment === u.key ? "bg-slate-900 text-white" : "bg-slate-50 active:bg-slate-100")}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-bold">{[kindFrom(u.category) || u.category, u.brand].filter(Boolean).join(" · ") || "Unit"}</span>
                    <span className={cn("block truncate font-mono text-[13.5px]", f.from_equipment === u.key ? "text-white/70" : "text-slate-500")}>
                      {[u.model, u.serial ? `S/N ${u.serial}` : null, u.year ? String(u.year) : null].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {f.from_equipment === u.key && <Check className="h-4 w-4 shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <div className="text-[12.5px] font-bold text-slate-700">What is it?</div>
        <div className="mt-1.5"><ReasonChips options={REMOVED_KINDS} value={f.category ?? ""} onPick={(v) => setF((x) => ({ ...x, category: v }))} /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={f.brand ?? ""} onChange={(e) => setF((x) => ({ ...x, brand: e.target.value }))} placeholder="Brand" className={input} />
        <input value={f.year_made ?? ""} inputMode="numeric" onChange={(e) => { const n = Number(e.target.value.replace(/\D/g, "").slice(0, 4)); setF((x) => ({ ...x, year_made: n || null })); }} placeholder="Year made" className={input} />
      </div>
      <input value={f.model ?? ""} onChange={(e) => setF((x) => ({ ...x, model: e.target.value }))} placeholder="Model" className={cn(input, "font-mono")} />
      <input value={f.serial ?? ""} onChange={(e) => setF((x) => ({ ...x, serial: e.target.value.toUpperCase() }))} placeholder="Serial" autoCapitalize="characters" className={cn(input, "font-mono uppercase placeholder:font-sans placeholder:normal-case")} />
      <div className="m-seg">
        {(["haul_away", "left_on_site"] as const).map((d) => (
          <button key={d} type="button" onClick={() => setF((x) => ({ ...x, disposition: d }))} className={cn(f.disposition === d && "on")}>{DISPOSITION_LABEL[d]}</button>
        ))}
      </div>
      <input value={f.notes ?? ""} onChange={(e) => setF((x) => ({ ...x, notes: e.target.value }))} placeholder="Notes" className={input} />
      {showRec && (
        <>
          <RecoveryFields value={rec} onChange={setRec} />
          {!recoveryReady(rec) && <p className="px-1 text-[12px] text-slate-500">Not recovered yet? Save the unit — log it later from Coming Out.</p>}
        </>
      )}
      {holdsRef && !showRec && (
        <button type="button" onClick={() => setRecOpen(true)} className="m-btn m-btn-soft w-full"><Snowflake className="h-4 w-4" /> Log Refrigerant Recovered</button>
      )}
      {add.isError && <p className="text-center text-[12px] font-semibold text-rose-600">Didn&apos;t save — check the signal and try again.</p>}
    </Sheet>
  );
}

/* ------------------------------------------------------- before you start */

/** First thing on an install (Seth 2026-09-24: before photos "should be a
 *  prompt fairly early in the process so they don't skip this"): photos of
 *  the old equipment + each old unit's data tag. A tag photo is read and
 *  fills that unit's Coming Out record, so one photo does both jobs. Folds to
 *  one green line once both are in. */
export function BeforeYouStartCard({ job, dispatchId }: { job: Job; dispatchId: string | null }) {
  const qc = useQueryClient();
  const { data: removed } = useRemovedEquipment(job.id);
  const { data: files } = useJobFilesForBefore(job.id);
  const [tagOpen, setTagOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const before = (files ?? []).filter((f) => /^before/i.test(f.caption ?? "")).length;
  const oldUnits = (removed ?? []).length;
  const done = before > 0 && oldUnits > 0;

  const onPhotos = async (list: FileList | null) => {
    if (!list?.length) return;
    setBusy(true);
    setErr(null);
    try {
      for (const file of Array.from(list)) {
        await uploadOneFile({ custno: job.custno, locno: job.locno, job_id: job.id, caption: "Before — old equipment", doc_tag: "photo", origin: "tech" }, file);
      }
      haptic("success");
      void qc.invalidateQueries({ queryKey: ["files"] });
    } catch {
      setErr("A photo didn't upload — check the signal and try again.");
    } finally {
      setBusy(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-2.5 rounded-3xl bg-emerald-50 px-4 py-3 text-[13px] font-semibold text-emerald-800">
        <Check className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1">Before photos {before} · Old units {oldUnits}</span>
        <button type="button" onClick={() => photoRef.current?.click()} className="shrink-0 text-[12.5px] font-bold text-emerald-700">Add</button>
        <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => void onPhotos(e.target.files)} />
      </div>
    );
  }
  return (
    <section className={card}>
      <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><Camera className="h-4 w-4 text-slate-400" /> Before You Start</h4>
      <div className="mt-2.5 grid gap-2">
        <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => void onPhotos(e.target.files)} />
        <button type="button" disabled={busy} onClick={() => photoRef.current?.click()} className={cn("m-btn w-full", before ? "m-btn-soft" : "m-btn-tint")}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : before ? <Check className="h-4 w-4 text-emerald-600" /> : <Camera className="h-4 w-4" />}
          {before ? `Before Photos · ${before}` : "Photograph the Old Equipment"}
        </button>
        <button type="button" onClick={() => setTagOpen(true)} className={cn("m-btn w-full", oldUnits ? "m-btn-soft" : "m-btn-tint")}>
          {oldUnits ? <Check className="h-4 w-4 text-emerald-600" /> : <Truck className="h-4 w-4" />}
          {oldUnits ? `Old Units Logged · ${oldUnits}` : "Photograph Each Old Data Tag"}
        </button>
      </div>
      {err && <p className="mt-2 text-center text-[12px] font-semibold text-rose-600">{err}</p>}
      {tagOpen && <ComingOutSheet job={job} dispatchId={dispatchId} taken={new Set((removed ?? []).map((r) => r.from_equipment).filter(Boolean) as string[])} onClose={() => setTagOpen(false)} />}
    </section>
  );
}

/* --------------------------------------------------------------- changes */

export function ChangesStep({ job, dispatchId, techFirst, empno, moneyCard }: { job: Job; dispatchId: string | null; techFirst: string | null; empno: string | null; moneyCard?: React.ReactNode }) {
  const { data: mats } = useJobMaterials(job.id);
  const { data: cos } = useChangeOrders(job.id);
  const removeField = useRemoveFieldMaterial();
  const [editing, setEditing] = useState<string | null>(null);
  const [addingMat, setAddingMat] = useState(false);
  const [selling, setSelling] = useState(false);
  const all = mats ?? [];
  // MATERIAL-REQ-01: what the crew asked the warehouse for is on the list too (not the cancelled lines).
  const planned = all.filter((m) => (m.source ?? "office") === "office" || (m.source === "request" && m.pull_status !== "cancelled"));
  const fieldAdds = all.filter((m) => m.source === "field");
  const fieldCos = (cos ?? []).filter((c) => c.source === "field" && c.status !== "rejected");
  const officeCos = (cos ?? []).filter((c) => (c.source ?? "office") === "office" && c.status === "approved");

  return (
    <>
      <section className={card}>
        <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><ClipboardList className="h-4 w-4 text-slate-400" /> Materials</h4>
        {planned.length === 0 ? (
          <p className="mt-2 rounded-2xl bg-slate-50 px-3 py-2.5 text-xs text-slate-500">No materials planned on this job.</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100">
            {planned.map((m) => (
              editing === m.id
                ? <MaterialEditor key={m.id} m={m} onDone={() => setEditing(null)} />
                : (
                  <li key={m.id}>
                    <button type="button" onClick={() => { haptic("light"); setEditing(m.id); }} className="flex w-full items-center gap-2.5 py-2.5 text-left active:opacity-70">
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-semibold leading-snug text-slate-800">{m.description}</span>
                        <span className="block text-[11.5px] text-slate-500">{m.source === "request" ? "Requested" : "Planned"} {qtyText(Number(m.qty))}{m.unit ? ` ${m.unit}` : ""}</span>
                        {m.qty_actual != null && m.change_reason && <span className="block text-[11.5px] font-medium text-amber-700">{m.change_reason}</span>}
                      </span>
                      {m.qty_actual != null ? (
                        <span className="m-chip m-chip-warn shrink-0">Used {qtyText(Number(m.qty_actual))}{m.unit ? ` ${m.unit}` : ""}</span>
                      ) : (
                        <span className="shrink-0 text-[12px] font-semibold text-slate-400">As planned</span>
                      )}
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                    </button>
                  </li>
                )
            ))}
          </ul>
        )}
        {fieldAdds.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Added on site</div>
            <ul className="mt-1 space-y-1.5">
              {fieldAdds.map((m) => (
                <li key={m.id} className="flex items-start gap-2 rounded-2xl bg-slate-50 px-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] font-semibold text-slate-800">{m.description} <span className="font-normal text-slate-500">· {qtyText(Number(m.qty_actual ?? m.qty))}{m.unit ? ` ${m.unit}` : ""}</span></span>
                    {m.change_reason && <span className="block text-[11.5px] text-slate-500">{m.change_reason}{m.changed_by ? ` — ${who(m.changed_by)}` : ""}</span>}
                  </span>
                  <button type="button" disabled={removeField.isPending} onClick={() => removeField.mutate(m.id)} aria-label="Remove"
                    className="shrink-0 rounded-lg p-1.5 text-slate-400 active:bg-white"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <button type="button" onClick={() => setAddingMat(true)} className="m-btn m-btn-soft mt-3 w-full">
          <Plus className="h-4 w-4" /> Add a Material You Needed
        </button>
      </section>

      <section className={card}>
        <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><ShoppingBag className="h-4 w-4 text-slate-400" /> Add-Ons Sold Today</h4>
        {fieldCos.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {fieldCos.map((c) => (
              <li key={c.id} className="flex items-start gap-2.5 rounded-2xl bg-emerald-50/70 px-3 py-2.5">
                <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-bold text-slate-800">{c.description ?? "Add-on"}</span>
                  <span className="block text-[11.5px] text-emerald-800">
                    Signed{c.signed_name ? ` by ${firstName(c.signed_name)}` : ""}{c.sold_by_name ? ` · sold by ${c.sold_by_name}` : ""}{c.signed_at ? ` · ${whenShort(c.signed_at)}` : ""}
                  </span>
                </span>
                <span className="shrink-0 text-[14px] font-bold tabular-nums text-slate-900">{money(Number(c.amount))}</span>
              </li>
            ))}
          </ul>
        )}
        {officeCos.length > 0 && (
          <div className="mt-2">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Changes from the office</div>
            <ul className="mt-1 space-y-1">
              {officeCos.map((c) => (
                <li key={c.id} className="flex items-baseline justify-between gap-2 text-[12.5px]">
                  <span className="min-w-0 truncate text-slate-600">{c.description ?? "Change"}</span>
                  <span className="shrink-0 font-semibold tabular-nums text-slate-700">{money(Number(c.amount))}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <button type="button" onClick={() => setSelling(true)} className="m-btn m-btn-primary mt-3 w-full">
          <Plus className="h-4 w-4" /> Sell an Add-On
        </button>
      </section>

      {moneyCard}

      {addingMat && <AddMaterialSheet job={job} onClose={() => setAddingMat(false)} />}
      {selling && <SellAddonSheet job={job} dispatchId={dispatchId} techFirst={techFirst} empno={empno} onClose={() => setSelling(false)} />}
    </>
  );
}

function MaterialEditor({ m, onDone }: { m: JobMaterial; onDone: () => void }) {
  const set = useSetMaterialUsed();
  const planned = Number(m.qty) || 0;
  const [used, setUsed] = useState<number>(m.qty_actual != null ? Number(m.qty_actual) : planned);
  const [reason, setReason] = useState(m.change_reason ?? "");
  const differs = Math.abs(used - planned) > 1e-9;
  const step = planned >= 20 ? 5 : 1;
  return (
    <li className="-mx-1 my-1 rounded-2xl bg-slate-50 p-3">
      <div className="text-[14px] font-bold leading-snug text-slate-800">{m.description}</div>
      <div className="mt-0.5 text-[11.5px] text-slate-500">Planned {qtyText(planned)}{m.unit ? ` ${m.unit}` : ""} — how much did you use?</div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <Stepper value={used} onChange={setUsed} step={step} />
        {m.unit && <span className="text-[13px] font-semibold text-slate-500">{m.unit}</span>}
      </div>
      {differs && (
        <div className="mt-2.5">
          <div className="text-[12px] font-bold text-slate-700">Why?</div>
          <div className="mt-1.5"><ReasonChips options={MATERIAL_REASONS} value={reason} onPick={setReason} /></div>
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button type="button" onClick={onDone} className="m-btn m-btn-soft">Cancel</button>
        <button type="button" disabled={(differs && !reason) || set.isPending}
          onClick={() => set.mutate({ id: m.id, planned, used, reason: differs ? reason : null }, { onSuccess: () => { haptic("success"); onDone(); } })}
          className="m-btn m-btn-primary">
          {set.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {differs ? "Save" : "As Planned"}
        </button>
      </div>
      {set.isError && <p className="mt-1.5 text-center text-[12px] font-semibold text-rose-600">Didn&apos;t save — check the signal and try again.</p>}
    </li>
  );
}

/** Pricebook search for the phone — description + part number only (never cost). */
function ItemPicker({ q, setQ, onPick, showPrice }: { q: string; setQ: (s: string) => void; onPick: (h: ItemHit) => void; showPrice?: boolean }) {
  const { data: hits, isFetching } = useItemSearch(q);
  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the pricebook — name, model or part #"
          className={cn(input, "pl-9")} autoFocus />
        {isFetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      {q.trim().length >= 2 && (
        <ul className="mt-2 divide-y divide-slate-100 rounded-2xl bg-white ring-1 ring-slate-200">
          {(hits ?? []).slice(0, 12).map((h) => {
            const price = Number(h.base_price) || 0;
            const noPrice = showPrice && price <= 0;
            return (
              <li key={h.id}>
                <button type="button" disabled={noPrice} onClick={() => { haptic("light"); onPick(h); }}
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left active:bg-slate-50 disabled:opacity-50">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold text-slate-800">{h.description || h.model || h.part_number}</span>
                    <span className="block truncate font-mono text-[11px] text-slate-400">{[h.part_number, h.manufacturer].filter(Boolean).join(" · ")}</span>
                  </span>
                  {showPrice && <span className={cn("shrink-0 text-[13px] font-bold tabular-nums", noPrice ? "text-slate-400" : "text-slate-800")}>{noPrice ? "no price" : money(price)}</span>}
                </button>
              </li>
            );
          })}
          {!isFetching && (hits ?? []).length === 0 && <li className="px-3 py-3 text-center text-[12.5px] text-slate-400">Nothing found — try another word.</li>}
        </ul>
      )}
    </div>
  );
}

function AddMaterialSheet({ job, onClose }: { job: Job; onClose: () => void }) {
  const add = useAddFieldMaterial();
  const [q, setQ] = useState("");
  const [item, setItem] = useState<ItemHit | null>(null);
  const [manual, setManual] = useState(false);
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState("");
  const [reason, setReason] = useState("");
  const reasons = MATERIAL_REASONS.filter((r) => !/^Shorter/.test(r));
  // REFRIG-01: refrigerant is weighed in on Log the Charge — added here too it would count twice.
  const isRefrigerant = (item?.category ?? "").toLowerCase() === "refrigerant";
  const chosen = !!item || (manual && desc.trim().length > 1);
  const ok = chosen && !isRefrigerant && qty > 0 && !!reason && !add.isPending;
  return (
    <Sheet title="Add a Material" onClose={onClose}
      footer={
        <button type="button" disabled={!ok}
          onClick={() => add.mutate({ jobId: job.id, itemId: item?.id ?? null, description: item ? null : desc.trim(), qty, unit: unit.trim() || null, reason },
            { onSuccess: () => { haptic("success"); onClose(); } })}
          className="m-btn m-btn-primary m-btn-big w-full">
          {add.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />} Add It to the Job
        </button>
      }>
      {item ? (
        <div className="flex items-start gap-2.5 rounded-2xl bg-white p-3 ring-1 ring-slate-200">
          <Package className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1">
            <span className="block text-[14px] font-bold text-slate-800">{item.description || item.part_number}</span>
            {item.part_number && <span className="block font-mono text-[11.5px] text-slate-400">{item.part_number}</span>}
          </span>
          <button type="button" onClick={() => setItem(null)} className="shrink-0 text-[12.5px] font-semibold text-brand-700">Change</button>
        </div>
      ) : manual ? (
        <div className="space-y-2 rounded-2xl bg-white p-3 ring-1 ring-slate-200">
          <div className="flex items-center justify-between">
            <span className="text-[12.5px] font-bold text-slate-700">Not in the pricebook</span>
            <button type="button" onClick={() => setManual(false)} className="text-[12.5px] font-semibold text-brand-700">Search instead</button>
          </div>
          <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is it? e.g. 3/4 in. PVC elbow" className={input} autoFocus />
        </div>
      ) : (
        <>
          <ItemPicker q={q} setQ={setQ} onPick={(h) => setItem(h)} />
          <button type="button" onClick={() => { setManual(true); setDesc(q); }} className="w-full py-2 text-center text-[13px] font-semibold text-brand-700">
            Not in the pricebook? Type it in →
          </button>
        </>
      )}
      {isRefrigerant && (
        <p className="rounded-2xl bg-amber-50 px-3 py-2.5 text-[13px] font-semibold leading-snug text-amber-900 ring-1 ring-amber-200">
          Refrigerant goes on Log the Charge — Equipment step, under the outdoor unit. That&apos;s where the pounds come off stock and land on the job, so it counts once.
        </p>
      )}
      {chosen && !isRefrigerant && (
        <>
          <div className="flex items-center justify-between gap-2">
            <Stepper value={qty} onChange={setQty} />
            <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="unit (ft, ea…)" className={cn(input, "w-32")} />
          </div>
          <div>
            <div className="text-[12.5px] font-bold text-slate-700">Why did you need it?</div>
            <div className="mt-1.5"><ReasonChips options={reasons} value={reason} onPick={setReason} /></div>
          </div>
        </>
      )}
      {add.isError && <p className="text-center text-[12px] font-semibold text-rose-600">{(add.error as Error)?.message}</p>}
    </Sheet>
  );
}

/** Sell an add-on: pricebook price (the iPad can't change it) or a custom
 *  line with a typed price → the customer signs → approved change order. */
function SellAddonSheet({ job, dispatchId, techFirst, empno, onClose }: { job: Job; dispatchId: string | null; techFirst: string | null; empno: string | null; onClose: () => void }) {
  const sell = useSellFieldAddon();
  const [q, setQ] = useState("");
  const [item, setItem] = useState<ItemHit | null>(null);
  const [custom, setCustom] = useState(false);
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  const [signing, setSigning] = useState(false);
  const unitPrice = item ? Number(item.base_price) || 0 : Number(price) || 0;
  const label = item ? (item.description || item.part_number || "Add-on") : desc.trim();
  const total = useMemo(() => Math.round(qty * unitPrice * 100) / 100, [qty, unitPrice]);
  const ready = !!label && unitPrice > 0 && qty > 0;
  const customer = job.customer_name?.trim() || "The customer";
  const first = firstName(customer) ?? "The customer";

  if (signing) {
    return (
      <SignaturePad
        customerName={customer}
        total={total}
        title="Approve an Add-On"
        subtitle={`${label}${qty !== 1 ? ` × ${qtyText(qty)}` : ""} · ${money(total)}`}
        consent={`By signing, ${first} approves adding ${label}${qty !== 1 ? ` × ${qtyText(qty)}` : ""} for ${money(total)} to the price of this installation.`}
        doneLabel="Approve"
        onClose={() => setSigning(false)}
        onDone={(sig) => {
          setSigning(false);
          sell.mutate(
            { jobId: job.id, itemId: item?.id ?? null, description: item ? null : desc.trim(), qty, unitPrice: item ? null : unitPrice,
              signature: sig, signedName: customer, dispatchId, soldByEmpno: empno, soldByName: techFirst },
            { onSuccess: () => { haptic("success"); onClose(); } },
          );
        }}
      />
    );
  }

  return (
    <Sheet title="Sell an Add-On" sub={customer} onClose={onClose}
      footer={
        <button type="button" disabled={!ready || sell.isPending} onClick={() => setSigning(true)} className="m-btn m-btn-primary m-btn-big w-full">
          {sell.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <PenLine className="h-[18px] w-[18px]" />}
          {ready ? `Customer Signs — ${money(total)}` : "Pick the Add-On First"}
        </button>
      }>
      {!item && !custom ? (
        <>
          <ItemPicker q={q} setQ={setQ} onPick={(h) => setItem(h)} showPrice />
          <button type="button" onClick={() => { setCustom(true); setDesc(q); }} className="w-full py-2 text-center text-[13px] font-semibold text-brand-700">
            Not in the pricebook? Price it yourself →
          </button>
        </>
      ) : (
        <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-200">
          {item ? (
            <div className="flex items-start gap-2.5">
              <ShoppingBag className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-bold text-slate-800">{label}</span>
                <span className="block text-[12px] text-slate-500">{money(unitPrice)} each</span>
              </span>
              <button type="button" onClick={() => setItem(null)} className="shrink-0 text-[12.5px] font-semibold text-brand-700">Change</button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] font-bold text-slate-700">Custom add-on</span>
                <button type="button" onClick={() => setCustom(false)} className="text-[12.5px] font-semibold text-brand-700">Search instead</button>
              </div>
              <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is it? e.g. Extra return air drop" className={input} />
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-slate-400">$</span>
                <input value={price} inputMode="decimal" onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Price to the customer" className={cn(input, "pl-7 tabular-nums")} />
              </div>
            </div>
          )}
        </div>
      )}
      {(item || custom) && (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-bold text-slate-700">How many?</span>
            <Stepper value={qty} onChange={(n) => setQty(Math.max(1, n))} />
          </div>
          {ready && (
            <div className="rounded-2xl bg-slate-900 px-4 py-3 text-white">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60">Adds to {first}&apos;s total</div>
              <div className="text-[24px] font-extrabold tabular-nums tracking-tight">{money(total)}</div>
            </div>
          )}
          {techFirst && <p className="px-1 text-[12px] font-semibold text-slate-500">Sold by {techFirst}</p>}
        </>
      )}
      {sell.isError && <p className="text-center text-[12.5px] font-semibold text-rose-600">{(sell.error as Error)?.message}</p>}
    </Sheet>
  );
}
